import {
  PhoneCallInterface,
  PhoneCallListItem
} from '@/interfaces/PhoneCall.interface';
import type { ListDataParams, ListDataResponse } from '../types';
import BaseApiClient from '../BaseApiClient';
import ApiClient from '../ApiClient';
import { AxiosResponse } from 'axios';

export class ApiPhoneCall extends BaseApiClient {
  private api!: ApiClient;

  setApiClient(api: ApiClient) {
    this.api = api;
  }
  /**
   * A page of calls for the tenant, newest first.
   */
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<PhoneCallListItem>> {
    const params = { ...listData };
    if (searchTerm) {
      if (!params.filters) params.filters = {};
      params.filters.number = searchTerm;
    }

    const res = await this.client.getList('/phone-calls', params);
    if (!res.data) {
      throw new Error('Invalid response');
    }

    return {
      items: res.data.data ?? [],
      pagination: {
        page: res.data.meta?.current_page,
        perPage: res.data.meta?.per_page,
        total: res.data.meta?.total
      }
    };
  }

  /**
   * Gets all items
   */
  async get(id: number): Promise<PhoneCallInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/phone-calls/${id}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  /**
   * Hands a live inbound call to another user. Only inbound calls can be transferred - the API
   * rejects outbound ones, because they are recorded and transferring would split one
   * conversation into two recordings.
   */
  async transfer(id: number | string, userId: number): Promise<PhoneCallInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/phone-calls/${id}/transfer`, { user_id: userId })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async downloadSummaryOrTranscript(
    id: number | string,
    tab: string
  ): Promise<AxiosResponse<Blob>> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/phone-calls/${id}/${tab}`, {
          responseType: 'blob'
        })
        .then((res) => {
          if (res.data) {
            resolve(res);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { phoneCallQueryKeys } from './query-keys';
export {
  usePhoneCallDownloadSummaryOrTranscriptQuery,
  usePhoneCallQuery
} from './use-phone-call-queries';
export type { PhoneCallDownloadQueryParams } from './use-phone-call-queries';
